import { useEffect, useMemo, useState } from 'react';
import { SettingRow } from '@vnetwork/ui/layout';
import { Card, CardHeader, CardTitle, CardDescription, CardBody, Switch, Input, Textarea } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useCdnConfig, useUpdateCdnConfig, useDeleteCdnConfig } from '@/api/manticore';
import { errorMessage } from '@/api/error-message';
import { trackFeatureAction } from '@/lib/analytics-events';
import { ConfigFormShell, jsonEqual } from '../../_shared/ConfigFormShell';
import { CdnAspectDrift } from '../../_shared/VendorDrift';

interface Form {
  enabled: boolean;
  threshold_window_secs: number;
  verified_threshold_count: number;
  unverified_threshold_count: number;
  verified_burst_size: number;
  unverified_burst_size: number;
  level1_count: number;
  level2_count: number;
  level3_count: number;
  block_duration_secs: number;
  cookie_ttl_secs: number;
  bypass_verified_bots: boolean;
  bypass_cidrs: string[];
}
const DEFAULTS: Form = {
  enabled: false,
  threshold_window_secs: 60,
  verified_threshold_count: 1000,
  unverified_threshold_count: 100,
  verified_burst_size: 50,
  unverified_burst_size: 20,
  level1_count: 100,
  level2_count: 200,
  level3_count: 400,
  block_duration_secs: 300,
  cookie_ttl_secs: 86400,
  bypass_verified_bots: true,
  bypass_cidrs: [],
};
const N = (v: unknown, d: number) => (v == null ? d : Number(v));

function hydrate(c: Record<string, unknown>): Form {
  return {
    enabled: Boolean(c.enabled),
    threshold_window_secs: N(c.threshold_window_secs, 60),
    verified_threshold_count: N(c.verified_threshold_count, 1000),
    unverified_threshold_count: N(c.unverified_threshold_count, 100),
    verified_burst_size: N(c.verified_burst_size, 50),
    unverified_burst_size: N(c.unverified_burst_size, 20),
    level1_count: N(c.level1_count, 100),
    level2_count: N(c.level2_count, 200),
    level3_count: N(c.level3_count, 400),
    block_duration_secs: N(c.block_duration_secs, 300),
    cookie_ttl_secs: N(c.cookie_ttl_secs, 86400),
    bypass_verified_bots: c.bypass_verified_bots == null ? true : Boolean(c.bypass_verified_bots),
    bypass_cidrs: Array.isArray(c.bypass_cidrs) ? (c.bypass_cidrs as string[]) : [],
  };
}

export function BotManagementTab({ domain, domainId }: { domain: string; domainId: string }) {
  const { show } = useToast();
  const { data: cfg, isLoading } = useCdnConfig(domainId, 'bot-mgmt', { vendorMarkers: ['enabled', 'threshold_window_secs'] });
  const isConfigured = !!cfg;
  const [form, setForm] = useState<Form>(DEFAULTS);
  const [snap, setSnap] = useState<Form>(DEFAULTS);
  useEffect(() => {
    const next = cfg ? hydrate(cfg) : DEFAULTS;
    setForm(next);
    setSnap(next);
  }, [cfg]);
  const dirty = useMemo(() => !jsonEqual(form, snap), [form, snap]);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const update = useUpdateCdnConfig(domainId, 'bot-mgmt', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'bot_mgmt', isConfigured ? 'edit' : 'create');
      show({ tone: 'success', title: 'Bot management saved' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not save', description: errorMessage(e, 'Could not save') }),
  });
  const remove = useDeleteCdnConfig(domainId, 'bot-mgmt', {
    onSuccess: () => {
      trackFeatureAction('cdn', 'bot_mgmt', 'delete');
      show({ tone: 'success', title: 'Bot management removed' });
    },
    onError: (e) => show({ tone: 'danger', title: 'Could not remove', description: errorMessage(e, 'Could not remove') }),
  });
  const busy = update.isPending || remove.isPending;

  const numField = (label: string, key: keyof Form, hint?: string) => (
    <Field label={label} description={hint}>
      {(p) => <Input {...p} type="number" value={String(form[key])} onChange={(e) => set(key, (Number(e.target.value) || 0) as Form[typeof key])} />}
    </Field>
  );
  const grid: React.CSSProperties = { display: 'grid', gap: 'var(--vn-space-4)', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' };

  return (
    <ConfigFormShell
      title="Bot Management"
      description={`Behavioral bot detection and escalation for ${domain}.`}
      isLoading={isLoading}
      isConfigured={isConfigured}
      dirty={dirty}
      busy={busy}
      saving={update.isPending}
      statusLabel={form.enabled ? 'Enabled' : undefined}
      onSave={() => update.mutate(form as unknown as Record<string, unknown>)}
      onRemove={() => remove.mutate()}
      drift={<CdnAspectDrift domainId={domainId} path="bot-mgmt" />}
      onCancel={() => setForm(snap)}
    >
      <Card>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <SettingRow title="Enabled" description="Track verified vs unverified clients via the __vc cookie and escalate on abuse." control={<Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Thresholds &amp; windows</CardTitle>
        </CardHeader>
        <CardBody>
          <div style={grid}>
            {numField('Detection window (s)', 'threshold_window_secs')}
            {numField('Verified threshold', 'verified_threshold_count')}
            {numField('Unverified threshold', 'unverified_threshold_count')}
            {numField('Verified burst', 'verified_burst_size')}
            {numField('Unverified burst', 'unverified_burst_size')}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Escalation tiers</CardTitle>
          <CardDescription>L1 = JS challenge (429), L2 = HTML challenge, L3 = hard block.</CardDescription>
        </CardHeader>
        <CardBody>
          <div style={grid}>
            {numField('Level 1 count', 'level1_count')}
            {numField('Level 2 count', 'level2_count')}
            {numField('Level 3 count', 'level3_count')}
            {numField('Block duration (s)', 'block_duration_secs')}
            {numField('Cookie TTL (s)', 'cookie_ttl_secs')}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bypass</CardTitle>
        </CardHeader>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <SettingRow title="Bypass verified bots" control={<Switch checked={form.bypass_verified_bots} onCheckedChange={(v) => set('bypass_verified_bots', v)} />} />
          <Field label="Bypass CIDRs" description="One CIDR per line.">
            {(p) => <Textarea {...p} rows={3} value={form.bypass_cidrs.join('\n')} placeholder="10.0.0.0/8" onChange={(e) => set('bypass_cidrs', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 'var(--vn-font-size-sm)' }} />}
          </Field>
        </CardBody>
      </Card>

      {!form.enabled && <Alert tone="info">Bot management is disabled. Enable it to start scoring traffic.</Alert>}
    </ConfigFormShell>
  );
}
